import React, { useState, useEffect } from 'react';
import { Button, Container, Row, Col, Form, Card } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import Table from '../../Components/Table/Table';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { DataPullerService } from '../../Services/DataPullerService';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import TaskDetailsModal from './TaskDetailsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faDownload, faGlasses, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import './TaskListPage.css';
import { blobStorageSAS } from '../../apiConfig';
import { FileManagementService } from '../../Services/FileManagementService';
import StorageProgressBar from '../../Components/StorageProgressBar/StorageProgressBar';
import TaskSchedulerModal from './TaskSchedulerModal';
import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';

const TaskListPageContent = () => {
    const readOnly = useReadOnly(Permissions.Extractions.Manual);
    const { instance, accounts, inProgress } = useMsal();
    const { navigateToNewTask } = useOutletContext();
    const [tasks, setTasks] = useState([]);
    const [filters, setFilters] = useState({
        StartDate: '',
        EndDate: '',
        NameContains: '',
        Status: ''
    });
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [storage, setStorage] = useState({ usedStorage: 0, storagePlan: { storageLimit: 1 } });

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchTasks();
        }
    }, [filters, inProgress, accounts, instance]);

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchStorage();
        }
    }, [inProgress, accounts, instance]);

    const fetchTasks = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const data = await DataPullerService.getTasks(filters, response.accessToken);
            setTasks(data);
        } catch (error) {
            console.error('Failed to fetch tasks:', error);
        }
    };

    const fetchStorage = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const userStorage = await FileManagementService.getUserStorage(response.accessToken);
            setStorage(userStorage);
        } catch (error) {
            console.error('Failed to load storage:', error);
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleSelectTask = async (taskId) => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const data = await DataPullerService.getTaskDetails(taskId, response.accessToken);
            setSelectedTask(data);
            setShowDetailsModal(true);
        } catch (error) {
            console.error('Failed to fetch parameter groups:', error);
        }
    }

    const handleScheduleTask = async () => {
        setShowScheduleModal(true);
    }

    const handleRefreshTask = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const data = await DataPullerService.getTaskDetails(selectedTask.taskId, response.accessToken);
            setSelectedTask(data);
        } catch (error) {
            console.error('Failed to fetch parameter groups:', error);
        }
    }

    const handleDownloadFile = (fileLink) => {
        try {
            const link = document.createElement('a');
            link.href = fileLink + blobStorageSAS;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {

        }
    }

    const getStatusBadgeClass = (status) => {
        if (status == 'Completed') return 'text-bg-success';
        else if (status == 'Ready') return 'text-bg-info';
        else if (status == 'Error') return 'text-bg-danger';
        else return 'text-bg-secondary';
    }

    const columns = React.useMemo(
        () => [
            { 
                header: 'ID', 
                accessorKey: 'taskId',
                meta: {
                    filterVariant: 'range',
                }, 
            },
            { 
                header: 'Name', 
                accessorKey: 'name' 
            },
            { 
                header: 'Start Date', 
                accessorKey: 'startDateTime', 
                cell: info => formatGuatemalanDateTime(info.getValue()),
                meta: {
                    filterVariant: 'date-range',
                },
                filterFn: 'dateBetweenFilterFn'
            },
            { 
                header: 'End Date', 
                accessorKey: 'endDateTime', 
                cell: info => formatGuatemalanDateTime(info.getValue()),
                meta: {
                    filterVariant: 'date-range',
                },
                filterFm: 'dateBetweenFilterFn'
            },
            { 
                header: 'Status', 
                accessorKey: 'status', 
                cell: info => <span className={`badge ${getStatusBadgeClass(info.getValue())}`}>{info.getValue()}</span> ,
                meta: {
                    filterVariant: 'select',
                },
            },
            {
                header: 'Details',
                id: 'details',
                cell: ({ cell, row }) => <div className='text-center'><Button size='sm' variant='outline-dark' onClick={() => handleSelectTask(row.original.taskId)}><FontAwesomeIcon icon={faGlasses} /></Button></div>,
                footer: props => props.column.id,
            },
            {
                header: 'File',
                id: 'fileLink',
                //cell: info => info.row.original.fileLink ? <a href={info.row.original.fileLink} target="_blank" rel="noopener noreferrer">Download</a> : ''
                cell: ({ cell, row }) => row.original.fileLink ? <div className='text-center'><Button size='sm' variant='outline-primary' onClick={() => handleDownloadFile(row.original.fileLink)}><FontAwesomeIcon icon={faDownload}/></Button></div>  : ''
            },
        ],
        []
    );

    const memoizedData = React.useMemo(() => tasks, [tasks]);

    return (
        <Container fluid className='h-100 d-flex flex-column TaskListPage'>
            {/* <div className='text-left'>
                <h5 className='mb-0'>Data Selector</h5>
            </div> */}
            <div className='d-flex justify-content-end w-100'>
                <button className='btn btn-xl btn-outline-primary fw-bold me-2' onClick={fetchTasks}>
                    <FontAwesomeIcon icon={faRefresh} />
                </button>
                <button className='btn btn-xl btn-primary fw-bold' disabled={readOnly} onClick={navigateToNewTask}>New Task</button>
            </div>
            <Row className='my-4 tasklist-content'>
                {/* <Col className='filters-col' sm={3}>
                    <Form>
                        <Card className='bg-light-gray p-3 border-0'>
                            <h6 className='mb-4'>Select Dates</h6>
                            <Form.Group className='mb-3'>
                                <Form.Label>Start Date</Form.Label>
                                <Form.Control size='sm' className='form-control-mnml' type="date" name="StartDate" value={filters.StartDate} onChange={handleFilterChange} />
                            </Form.Group>
                            <Form.Group className='mb-3'>
                                <Form.Label>End Date</Form.Label>
                                <Form.Control size='sm' className='form-control-mnml' type="date" name="EndDate" value={filters.EndDate} onChange={handleFilterChange} />
                            </Form.Group>
                        </Card>
                        <Card className='bg-light-gray p-3 mt-3 border-0'>
                            <h6 className='mb-4'>Task Name</h6>
                            <Form.Group className='mb-3'>
                                <Form.Control size='sm' className='form-control-mnml' type="text" name="NameContains" placeholder='Name' value={filters.NameContains} onChange={handleFilterChange} />
                            </Form.Group>
                        </Card>
                        <Card className='bg-light-gray p-3 mt-3 border-0'>
                            <h6 className='mb-4'>Status</h6>
                            <Form.Group className='mb-3'>
                                <Form.Select name='Status' size='sm' className='form-control-mnml' value={filters.Status} onChange={handleFilterChange}>
                                    <option value="">All</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Completed">Completed</option>
                                </Form.Select>
                            </Form.Group>
                        </Card>


                    </Form>
                </Col> */}
                <Col className='table-col' sm={12}>
                    <Table columns={columns} data={memoizedData} tableClass="table-hover" containerClass="table-responsive" headerClass="table-light" />
                </Col>
                <TaskDetailsModal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} task={selectedTask} onRefresh={handleRefreshTask} />
                <TaskSchedulerModal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} />
            </Row>
            {storage && <StorageProgressBar storage={storage} />}
        </Container>
    );
};

const TaskListPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Data Selector">
                <TaskListPageContent />

            </PageLayout2>
        </MsalAuthenticationTemplate>
    )
}

export default TaskListPage;
